import { HTMLProps, ReactNode } from "react";

import {
  DialogContent,
  DialogOverlay,
  DialogOverlayProps,
} from "@reach/dialog";
import { VisuallyHidden } from "@reach/visually-hidden";

import "@reach/dialog/styles.css";
import { useGetProduct } from "queries/product-queries";
import { SSR_CONFIGURATION_ERROR } from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";

import styles from "./Modal.module.scss";

export type ModalProps = (Omit<HTMLProps<HTMLDivElement>, "ref" | "as"> &
  DialogOverlayProps) & {
  children: ReactNode;
  // Override onDismiss type which is incorrectly requiring an event
  onDismiss: (data?: qs.ParsedQs) => void;
  title: string;
};

/**
 * Generic wrapper around Reach UI Dialog.
 *
 * Handles keyboard focus, and dismissal on off-click and escape key.
 */
export default function Modal({
  children,
  className,
  title,
  ...props
}: ModalProps) {
  const { data: product } = useGetProduct();

  const interpolate = useInterpolate();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  return (
    <DialogOverlay allowPinchZoom className={styles.overlay} {...props}>
      <DialogContent className={[styles.modal, className].join(" ")}>
        <div className={styles.header}>
          <button className={styles.cancelButton} onClick={props.onDismiss}>
            {interpolate(
              product.content.canvas.form_cancel_button_label ||
                CANVAS_FALLBACK_CONTENT.form_cancel_button_label
            )}
          </button>
          <VisuallyHidden>{title}</VisuallyHidden>
        </div>

        <div className={styles.content}>{children}</div>
      </DialogContent>
    </DialogOverlay>
  );
}
